import React from "react";
import { Link } from "@heroui/link";

import { OrderDetail } from "../../types/orders";

interface OrderInfoProps {
  order: OrderDetail;
  formatCurrency: (amount: number) => string;
}

const OrderInfo: React.FC<OrderInfoProps> = ({ order, formatCurrency }) => {
  // Get status styles
  const getStatusStyles = (statusName: string) => {
    const statusMap: Record<string, { bg: string; text: string }> = {
      new: {
        bg: "bg-blue-100 dark:bg-blue-900/20",
        text: "text-blue-800 dark:text-blue-300",
      },
      processing: {
        bg: "bg-yellow-100 dark:bg-yellow-900/20",
        text: "text-yellow-800 dark:text-yellow-300",
      },
      ready: {
        bg: "bg-green-100 dark:bg-green-900/20",
        text: "text-green-800 dark:text-green-300",
      },
      out_for_delivery: {
        bg: "bg-purple-100 dark:bg-purple-900/20",
        text: "text-purple-800 dark:text-purple-300",
      },
      delivered: {
        bg: "bg-green-100 dark:bg-green-900/20",
        text: "text-green-800 dark:text-green-300",
      },
      picked_up: {
        bg: "bg-green-100 dark:bg-green-900/20",
        text: "text-green-800 dark:text-green-300",
      },
      cancelled: {
        bg: "bg-red-100 dark:bg-red-900/20",
        text: "text-red-800 dark:text-red-300",
      },
    };

    const lowerCaseStatus = statusName.toLowerCase().replace(/ /g, "_");

    return (
      statusMap[lowerCaseStatus] || {
        bg: "bg-gray-100 dark:bg-gray-700",
        text: "text-gray-800 dark:text-gray-300",
      }
    );
  };

  const statusStyle = getStatusStyles(order.status_name);

  // Format dates
  const formatDateTime = (dateString: string | null) => {
    if (!dateString) return "N/A";

    return new Date(dateString).toLocaleString();
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
        <h3 className="font-semibold text-lg text-gray-900 dark:text-white">
          Order Information
        </h3>
      </div>

      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Order Details */}
          <div className="space-y-4">
            <div>
              <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                Status
              </h4>
              <div
                className={`inline-flex px-3 py-1 rounded-full text-sm font-medium ${statusStyle.bg} ${statusStyle.text}`}
              >
                {order.status_name}
              </div>
            </div>

            <div>
              <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                Order Type
              </h4>
              <p className="text-gray-900 dark:text-white">
                {order.order_type === "pickup" ? "Pickup" : "Delivery"}
              </p>
            </div>

            <div>
              <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                Payment Method
              </h4>
              <p className="text-gray-900 dark:text-white">
                {order.payment_method_name}
              </p>
            </div>

            <div>
              <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                Payment Status
              </h4>
              <p
                className={
                  order.is_paid
                    ? "text-green-600 dark:text-green-400"
                    : "text-red-600 dark:text-red-400"
                }
              >
                {order.is_paid ? "Paid" : "Unpaid"}
              </p>
            </div>

            {order.scheduled_time && (
              <div>
                <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                  Scheduled Time
                </h4>
                <p className="text-gray-900 dark:text-white">
                  {formatDateTime(order.scheduled_time)}
                </p>
              </div>
            )}

            {order.estimated_ready_time && (
              <div>
                <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                  Estimated Ready Time
                </h4>
                <p className="text-gray-900 dark:text-white">
                  {formatDateTime(order.estimated_ready_time)}
                </p>
              </div>
            )}

            {order.notes && (
              <div>
                <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                  Order Notes
                </h4>
                <p className="text-gray-900 dark:text-white whitespace-pre-line">
                  {order.notes}
                </p>
              </div>
            )}

            {order.total && (
              <div>
                <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                  Total Amount
                </h4>
                <p className="text-gray-900 dark:text-white">
                  {formatCurrency(order.total)}
                </p>
              </div>
            )}
          </div>

          {/* Customer Info */}
          <div className="space-y-4">
            <div>
              <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                Customer
              </h4>
              <p className="text-gray-900 dark:text-white font-medium">
                {order.customer_name}
              </p>
              <p className="text-gray-600 dark:text-gray-400">
                <Link color="primary" href={`/customers/${order.customer_id}`}>
                  View Customer
                </Link>
              </p>
            </div>

            <div>
              <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                Phone
              </h4>
              <p className="text-gray-900 dark:text-white">
                {order.customer_phone}
              </p>
            </div>

            {order.order_type === "delivery" && order.delivery_address_id && (
              <div>
                <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                  Delivery Address
                </h4>
                <div className="text-gray-900 dark:text-white">
                  {/* Display delivery address here - in a real app, you would fetch and display this */}
                  <p>Delivery Address ID: {order.delivery_address_id}</p>
                </div>
              </div>
            )}

            {order.delivery_assignment && (
              <div>
                <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                  Delivery Driver
                </h4>
                <p className="text-gray-900 dark:text-white">
                  {order.delivery_assignment.driver_name}
                </p>
                <p className="text-gray-600 dark:text-gray-400 text-sm">
                  Status: {order.delivery_assignment.status}
                </p>
                {order.delivery_assignment.delivered_at && (
                  <p className="text-gray-600 dark:text-gray-400 text-sm">
                    Delivered:{" "}
                    {formatDateTime(order.delivery_assignment.delivered_at)}
                  </p>
                )}
              </div>
            )}

            <div>
              <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                Created By
              </h4>
              <p className="text-gray-900 dark:text-white">
                {order.created_by_name}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderInfo;
